import { InfoRow } from "@components/info-row";
import { formatPlanName, formatSubjectType } from "@utils/data-display";
import { FormContainer } from "@components/forms/form-container";
import { LimitsReport } from "@components/limits-report";
import {
  createPlanCreationPayload,
  withSceneSeatLimit,
} from "@pages/create-plan/create-plan-creation-payload";
import { Box, Grid } from "@mui-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCreatePlan } from "@utils/hooks/plan/use-create-plan";
import {
  plansCreationStateSelector,
  resetPlanCreationState,
  setIsFetchingData,
} from "@store/plan-creation/plan-creation-slice";
import { ReactSetStateFunction } from "@utils/utility-types";
import { SubjectType } from "@custom-types/base-types";
import { DateTimeUtils } from "@stellar/utils";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";
import { devFeaturesStateSelector } from "@store/dev-features/dev-features-slice";
import {
  calculateContinuingPlanEndDate,
  getEndingNowDate,
  PLAN_DURATION_LABELS,
  planDurationInDays,
} from "@utils/plan-duration";
import {
  activePlanLimitsSelector,
  isEndingCopiedPlanSelector,
  planAppFeaturesSelector,
  planAppSettingsSelector,
  sceneSeatsToCreateSelector,
} from "@store/plan-creation/plan-creation-selectors";
import { PlanAppInfoRows } from "@components/plan-app-info-rows";
import { Alert } from "@flat-ui";

interface Props {
  /** Setter to show the summary page */
  setShouldShowSummary: ReactSetStateFunction<boolean>;

  /** Setter to allow or block leaving the page */
  setShouldBlockNavigation: ReactSetStateFunction<boolean>;
}

/** Shows the summary of the plan that is going to be created */
export function PlanCreationSummary({
  setShouldShowSummary,
  setShouldBlockNavigation,
}: Props): React.JSX.Element | null {
  const { shouldShowFullDateTime } = useAppSelector(devFeaturesStateSelector);
  const dispatch = useAppDispatch();

  const { trackEvent, limitsToEventProp } = useTrackEvent();

  const { createPlan } = useCreatePlan();

  const {
    selectedProduct,
    label,
    comments,
    tokensPerYear,
    copiedPlan,
    creationDates: { startDate, endDate, durationType, durationValue },
    planSubject: {
      planSubjectId,
      planSubjectType,
      planSubjectName,
      planSubscriberId,
    },
  } = useAppSelector(plansCreationStateSelector);
  const activeLimits = useAppSelector(activePlanLimitsSelector);
  const sceneSeatsToCreate = useAppSelector(sceneSeatsToCreateSelector);
  const appFeatures = useAppSelector(planAppFeaturesSelector);
  const appSettings = useAppSelector(planAppSettingsSelector);
  const shouldEndCopiedPlan = useAppSelector(isEndingCopiedPlanSelector);

  if (
    planSubjectId === null ||
    planSubjectType === null ||
    planSubscriberId === null
  ) {
    return null;
  }

  async function onCreateClicked(
    planSubjectId: string,
    planSubjectType: Exclude<SubjectType, "COMPANY_MEMBER">,
    planSubscriberId: string
  ): Promise<void> {
    trackEvent({
      name: PlanEvents.addPlan,
      props: {
        target: planSubjectType,
        durationInDays: planDurationInDays({ startDate, endDate }),
        durationType,
        startDate: DateTimeUtils.formatDateTimeInUtc({
          date: startDate,
          shouldShowFullDateTime,
        }),
        endDate: DateTimeUtils.formatDateTimeInUtc({
          date: endDate,
          shouldShowFullDateTime,
        }),
        planName: label,
        identifier: selectedProduct?.identifier,
        commentLength: comments?.length ?? 0,
        ...limitsToEventProp(
          withSceneSeatLimit(activeLimits, sceneSeatsToCreate)
        ),
        ...appSettings,
      },
    });

    dispatch(setIsFetchingData(true));
    setShouldBlockNavigation(false);

    // The copied plan ends when the copy starts, which cannot lie in the past. A copy that starts
    // today or "now" starts at the moment it is created instead, which is a moment ahead of now, so
    // that the end date of the copied plan still lies in the future when the request reaches the
    // backend, and the copy starts at the very same moment to follow it without a gap.
    const endingNowDate = getEndingNowDate();
    const copyStartDate =
      shouldEndCopiedPlan && startDate < endingNowDate ? endingNowDate : null;

    const payload = createPlanCreationPayload({
      shouldShowFullDateTime,
      isContinuingPlan: copiedPlan !== null,
      planSubjectId,
      subjectType: planSubjectType,
      selectedProduct,
      activeLimits,
      appFeatures,
      sceneSeatsCount: sceneSeatsToCreate,
      startDate: copyStartDate ?? startDate,
      // The remaining time of the copied plan always ends when the copied plan would have
      endDate:
        copyStartDate === null || copiedPlan?.durationMode === "remainingTime"
          ? endDate
          : calculateContinuingPlanEndDate({
              startDate: copyStartDate,
              durationType,
              durationValue,
            }),
      durationType,
      label,
      comments,
      planSubscriberId,
    });

    await createPlan(payload);

    dispatch(resetPlanCreationState());
  }

  return (
    <FormContainer
      createButtonText="Confirm"
      handleCreateClicked={() =>
        onCreateClicked(planSubjectId, planSubjectType, planSubscriberId)
      }
      cancelButtonText="Back"
      handleCancelClicked={() => setShouldShowSummary(false)}
    >
      <b>Confirm the plan and invoicing details.</b>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Box>
            <h2>Plan Details</h2>
            <Box sx={{ pl: "10px" }}>
              <InfoRow
                labelTextAlign="left"
                label={formatSubjectType(planSubjectType)}
                info={planSubjectName}
              />

              {copiedPlan && (
                <InfoRow
                  labelTextAlign="left"
                  label="Copy of"
                  info={formatPlanName(copiedPlan.plan)}
                />
              )}

              <InfoRow labelTextAlign="left" label="Product" info={label} />

              <PlanAppInfoRows
                labelTextAlign="left"
                appSettings={appSettings}
              />

              <InfoRow
                labelTextAlign="left"
                label="Plan Limits"
                info={
                  <LimitsReport
                    limits={activeLimits}
                    sceneSeatsCount={sceneSeatsToCreate}
                    tokensPerYear={tokensPerYear}
                  />
                }
              />

              <InfoRow
                labelTextAlign="left"
                label="Start Date"
                info={DateTimeUtils.formatDateTimeInUtc({
                  date: startDate,
                  shouldShowFullDateTime,
                  shouldShowUtcText: true,
                })}
              />
              <InfoRow
                labelTextAlign="left"
                label="End Date"
                info={
                  // An unlimited plan ends on a far future date only because it needs one
                  durationType === "unlimited"
                    ? PLAN_DURATION_LABELS.unlimited.option
                    : DateTimeUtils.formatDateTimeInUtc({
                        date: endDate,
                        shouldShowFullDateTime,
                        shouldShowUtcText: true,
                      })
                }
              />

              <InfoRow labelTextAlign="left" label="Comment" info={comments} />

              {shouldEndCopiedPlan && (
                <Alert
                  sx={{ mt: "24px" }}
                  variant="warning"
                  title="The original plan will end when the copy starts."
                />
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </FormContainer>
  );
}
